/**
 * 联机对战协议契约（客户端浏览器 / Cloudflare Worker + Durable Object 共用）。
 *
 * 本文件只包含类型、常量与纯函数，不依赖 three.js / DOM / Deno / Workers，
 * 双方（src/net/NetClient.ts 与 worker/）都从这里 import，保证消息结构一致。
 * 设计背景见 docs/ONLINE_PVP_DESIGN.md。
 *
 * 传输形态：
 * - 创建房间：HTTP `POST /rooms`，body 为 CreateRoomRequest，响应 CreateRoomResponse。
 * - 其余全部消息走 WebSocket：`GET /ws/:code`（:code 为 4 位房间码），JSON 文本帧。
 */

// ---------------------------------------------------------------------------
// 常量
// ---------------------------------------------------------------------------

/** WS 路由前缀，实际路径为 `${WS_PATH}/${roomCode}`。 */
export const WS_PATH = '/ws';
export const CREATE_ROOM_PATH = '/rooms';
/** 房间列表（HTTP GET，与创建房间同路径不同方法）。 */
export const LIST_ROOMS_PATH = '/rooms';
export const PROTOCOL_VERSION = 1;

/**
 * 对局状态快照发送频率（Hz）。
 * 2026-07-26 由 15 提到 20：间隔 66.7→50ms，配合插值缓冲 130→110ms，
 * 远端可感延迟降低约 20ms 且转向采样更细；4 人房带宽增量可忽略。
 */
export const SNAPSHOT_HZ = 20;
/** 快照发送间隔（秒），客户端节流用（与 SNAPSHOT_HZ 单一来源）。 */
export const SNAPSHOT_INTERVAL_S = 1 / SNAPSHOT_HZ;
/** 心跳间隔（ms）。 */
export const HEARTBEAT_INTERVAL_MS = 5000;
/** 心跳超时判定（ms），超过视为断线。 */
export const HEARTBEAT_TIMEOUT_MS = 15000;

/** 房间码字符表：去掉易混淆的 0/O/1/I/L。 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 4;

/** 规范化房间码（大写、去空白）。 */
export const normalizeRoomCode = (code: string): string => code.trim().toUpperCase();

// ---------------------------------------------------------------------------
// 模式与队伍
// ---------------------------------------------------------------------------

export type PvpMode = '1v1' | '2v2';

export const MODE_TEAM_SIZE: Record<PvpMode, number> = { '1v1': 1, '2v2': 2 };

/** 协议层的绝对队伍（与游戏内相对的 team:'player'|'enemy' 区分）。 */
export type TeamId = 'alpha' | 'bravo';

export const otherTeam = (team: TeamId): TeamId => (team === 'alpha' ? 'bravo' : 'alpha');

// ---------------------------------------------------------------------------
// 对局规则
// ---------------------------------------------------------------------------

export interface MatchRule {
  /** 常规时长（秒）。 */
  durationSec: number;
  /** 达到该击杀数立即获胜。 */
  killTarget: number;
  /** 阵亡后重生等待（秒）。 */
  respawnSec: number;
  /** 时间到平分时的骤死加时（秒），先杀者胜。 */
  suddenDeathSec: number;
}

export const MATCH_RULES: Record<PvpMode, MatchRule> = {
  '1v1': { durationSec: 180, killTarget: 5, respawnSec: 3, suddenDeathSec: 60 },
  '2v2': { durationSec: 180, killTarget: 8, respawnSec: 3, suddenDeathSec: 60 },
};

// ---------------------------------------------------------------------------
// 房间
// ---------------------------------------------------------------------------

export type RoomPhase = 'waiting' | 'countdown' | 'playing' | 'finished';

export interface RoomMember {
  playerId: string;
  name: string;
  team: TeamId;
  /** 队内序号（0 基），决定出生点。 */
  slot: number;
  ready: boolean;
  host: boolean;
  connected: boolean;
}

export interface RoomSnapshot {
  code: string;
  mode: PvpMode;
  phase: RoomPhase;
  members: RoomMember[];
}

// ---------------------------------------------------------------------------
// HTTP API
// ---------------------------------------------------------------------------

export interface CreateRoomRequest {
  mode: PvpMode;
  version: number;
}

export interface CreateRoomResponse {
  code: string;
  mode: PvpMode;
}

/** 房间列表条目（GET /rooms 返回；仅等待中且未满员的房间）。 */
export interface RoomListEntry {
  code: string;
  mode: PvpMode;
  /** 当前在房人数。 */
  players: number;
  /** 房间容量（mode 决定）。 */
  capacity: number;
}

export interface ListRoomsResponse {
  rooms: RoomListEntry[];
  serverTime: number;
}

// ---------------------------------------------------------------------------
// 对局内消息
// ---------------------------------------------------------------------------

/** TankSnapshot.buffs 位定义：速射增益（视觉复现用）。 */
export const BUFF_FLAG_RAPID = 1;
/** TankSnapshot.buffs 位定义：涡轮超载增益（视觉复现用）。 */
export const BUFF_FLAG_OVERDRIVE = 2;
/** TankSnapshot.buffs 位定义：辅助无人机激活（远端复现环绕无人机模型用）。 */
export const BUFF_FLAG_DRONE = 4;

/** 坦克状态快照（每个客户端 ~15Hz 广播自己的坦克，DO 纯转发）。 */
export interface TankSnapshot {
  /** 发送端本地比赛时钟（ms，从 match_start 起算）。 */
  t: number;
  x: number;
  z: number;
  /** 车体朝向（弧度）。 */
  body: number;
  /** 炮塔朝向（弧度）。 */
  turret: number;
  vx: number;
  vz: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  /** 护盾激活（用于远程端复现特效）。 */
  shield: boolean;
  /** 增益标记位（BUFF_FLAG_RAPID / BUFF_FLAG_OVERDRIVE 按位或；视觉复现用，可选，缺省按 0 处理）。 */
  buffs?: number;
}

/** 战斗事件：DO 转发给房间内其他人，并旁路计入结算统计。 */
export type CombatEvent =
  /** 开火：远端按相同参数在本地生成炮弹（视觉一致，命中由射手端检测）。 */
  | {
      kind: 'fire';
      x: number;
      z: number;
      angle: number;
      speed: number;
      damage: number;
      /** 散射发数（1 = 单发）。 */
      count: number;
      strong: boolean;
      /**
       * 发送端比赛时钟（ms，从 match_start 起算；可选，旧端缺省不快进）。
       * 接收端据此把弹道副本沿飞行方向快进传输延迟量，缩小两端弹道时差。
       */
      t?: number;
    }
  /** 命中：由射击者客户端发出，受害者客户端对自己的坦克扣血。 */
  | { kind: 'hit'; targetId: string; damage: number }
  /** 死亡：只能由死亡者客户端宣布；killerId=null 表示自杀/环境伤害。 */
  | { kind: 'died'; killerId: string | null }
  /** 布雷：远端在本地生成对应地雷实体。 */
  | { kind: 'mine'; x: number; z: number }
  /** 无人机开火：远端按出膛点与朝向在本地生成视觉弹道副本（命中仍由射手端检测上报 hit）。 */
  | { kind: 'drone'; x: number; z: number; angle: number };

// ---------------------------------------------------------------------------
// 结算
// ---------------------------------------------------------------------------

export interface PlayerMatchStats {
  playerId: string;
  name: string;
  team: TeamId;
  kills: number;
  deaths: number;
  damageDealt: number;
  mvp: boolean;
}

export type MatchEndReason = 'kill_target' | 'timeout' | 'sudden_death' | 'forfeit';

/** 对局中途离开（主动离开或断线被移出）的玩家。 */
export interface MatchQuitter {
  playerId: string;
  name: string;
}

export interface MatchResult {
  /** null = 平局。 */
  winnerTeam: TeamId | null;
  reason: MatchEndReason;
  durationSec: number;
  /** 结束时的双方击杀数 [alpha, bravo]。 */
  score: [number, number];
  stats: PlayerMatchStats[];
  /** 对局中途离开的玩家；无则省略该字段。 */
  quitters?: MatchQuitter[];
}

// ---------------------------------------------------------------------------
// 客户端 → 服务器（WebSocket）
// ---------------------------------------------------------------------------

export type ClientMessage =
  | { type: 'hello'; name: string; version: number; rejoinAs?: string }
  | { type: 'leave_room' }
  | { type: 'set_ready'; ready: boolean }
  /** 仅房主；要求满员且全员 ready。 */
  | { type: 'start_game' }
  /** playing 阶段高频消息：广播自己的坦克快照。 */
  | { type: 'state'; snapshot: TankSnapshot }
  /** 战斗事件（fire/hit/died/mine）。 */
  | { type: 'event'; event: CombatEvent }
  | { type: 'ping'; t: number };

// ---------------------------------------------------------------------------
// 服务器 → 客户端（WebSocket）
// ---------------------------------------------------------------------------

export type ServerMessage =
  /** hello 的应答：分配（或恢复）playerId，并附当前房间全量快照。 */
  | { type: 'welcome'; playerId: string; serverTime: number; room: RoomSnapshot }
  /** 房间有任何变化（进出/准备/阶段迁移）时全量推送。 */
  | { type: 'room_update'; room: RoomSnapshot }
  | { type: 'match_start'; startsAt: number; serverTime: number; room: RoomSnapshot }
  /** 转发他人快照。 */
  | { type: 'relay'; from: string; snapshot: TankSnapshot }
  /** 转发他人战斗事件。 */
  | { type: 'relay_event'; from: string; event: CombatEvent }
  | { type: 'player_left'; playerId: string }
  | { type: 'match_end'; result: MatchResult }
  | {
      type: 'error';
      code:
        | 'room_not_found'
        | 'room_full'
        | 'already_playing'
        | 'not_host'
        | 'not_ready'
        | 'not_in_room'
        | 'bad_request'
        | 'version_mismatch';
      message: string;
    }
  | { type: 'pong'; t: number; serverTime: number };

// ---------------------------------------------------------------------------
// 编解码
// ---------------------------------------------------------------------------

export const encodeMessage = (msg: ClientMessage | ServerMessage): string => JSON.stringify(msg);

export const decodeClientMessage = (raw: string): ClientMessage => JSON.parse(raw) as ClientMessage;

export const decodeServerMessage = (raw: string): ServerMessage => JSON.parse(raw) as ServerMessage;
